import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '../variants';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const GallerySection = () => {
  const [artists, setArtists] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load artists
      const artistsSnapshot = await getDocs(collection(db, 'artists'));
      const artistsData = artistsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArtists(artistsData);

      // Load gallery images
      const gallerySnapshot = await getDocs(collection(db, 'gallery'));
      const galleryData = gallerySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          src: data.original,
          original: data.original,
          width: data.width || 800,
          height: data.height || 600,
          artist: data.artist
        };
      });
      setGalleryImages(galleryData);
    } catch (error) {
      console.error('Error loading gallery data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter images by selected artist
  const filteredImages = selectedArtist === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.artist === selectedArtist);

  // Show only first 8 images for preview
  const previewImages = filteredImages.slice(0, 8);

  if (loading) {
    return (
      <section className='py-[40px] lg:pt-0 lg:pb-[160px]'>
        <div className='container mx-auto'>
          <div className='text-center'>Loading gallery...</div>
        </div>
      </section>
    );
  }

  return (
    <section className='py-[40px] lg:pt-0 lg:pb-[160px]'>
      <div className='container mx-auto'>
        <motion.h2
          variants={fadeIn('up')}
          initial='hidden'
          whileInView={'show'}
          viewport={{ once: false, amount: 0.6 }}
          className='h2 max-w-[370px] lg:mb-20 text-center mx-auto'
        >
          Our Gallery:
        </motion.h2>

        {/* Artist Filter Buttons */}
        <motion.div
          variants={fadeIn('up')}
          initial='hidden'
          whileInView={'show'}
          viewport={{ once: false, amount: 0.2 }}
          className='flex justify-center gap-4 mb-12 flex-wrap'
        >
          <button
            onClick={() => setSelectedArtist('All')}
            className={`btn ${selectedArtist === 'All' ? 'btn-dark' : 'btn-light'}`}
          >
            ALL
          </button>
          {artists.map((artist) => (
            <button
              key={artist.id}
              onClick={() => setSelectedArtist(artist.name)}
              className={`btn ${selectedArtist === artist.name ? 'btn-dark' : 'btn-light'}`}
            >
              {artist.name.toUpperCase()}
            </button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        {previewImages.length > 0 ? (
          <motion.div
            variants={fadeIn('up')}
            initial='hidden'
            whileInView={'show'}
            viewport={{ once: false, amount: 0.2 }}
            className='lg:gap-x-10 gap-y-6 grid grid-cols-2 lg:grid-cols-4 lg:gap-y-10 mb-10'
          >
            {previewImages.map((image, index) => (
              <motion.div
                key={image.id}
                variants={fadeIn('up')}
                initial='hidden'
                whileInView={'show'}
                viewport={{ once: false, amount: 0.1 }}
                className='relative overflow-hidden group rounded-lg shadow-lg'
              >
                <img
                  className='w-full h-[250px] lg:h-[320px] object-cover group-hover:scale-110 transition-transform duration-300'
                  src={image.original}
                  alt={`Gallery ${index + 1}`}
                />
                {/* Overlay with artist name */}
                <div className='absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
                  <span className='text-white font-semibold text-lg'>{image.artist}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className='text-center py-12'>
            <p className='text-gray-600'>
              {selectedArtist === 'All' 
                ? 'No images in gallery yet. Add some in the admin panel!' 
                : `No images by ${selectedArtist} yet.`}
            </p>
          </div>
        )}

        {/* View All Button */}
        <motion.div
          variants={fadeIn('up')}
          initial='hidden'
          whileInView={'show'}
          viewport={{ once: false, amount: 0.2 }}
          className='flex justify-center'
        >
          <Link 
            to='/gallery' 
            state={{ artist: selectedArtist }}
            className='btn btn-lg btn-dark'
          >
            View all
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default GallerySection;